import { NetworkStatus, useQuery, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  ControlledFacetedFilterControl,
  ControlledFactedFilterSummary,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  StatusUI,
  Txt,
  useQueryPersistedStateWithSchema,
  useRouter,
} from "@repo/ui";
import { prettyPrintEnum } from "@repo/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { X } from "lucide-react";
import { Suspense, useState } from "react";
import { PiBuildingOffice } from "react-icons/pi";
import { RiBuilding2Line, RiFilter3Line, RiPriceTagLine } from "react-icons/ri";
import { typeid } from "typeid-js";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { cashManagementStrategyStatusValues } from "~/gql/client/enumValues";
import type {
  AdminCashManagementStrategiesListQuery,
  CashManagementStrategiesPageFilter,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/cash-management-strategies")({
  head: () => opsHead("Cash Management Strategies"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

graphql(`
  fragment AdminCashManagementStrategyRow on CashManagementStrategy {
    id
    shortRef
    createdAt
    updatedAt
    status
    mdx
    organisation {
      id
      name
      squareLogoUrl
    }
    checklist {
      id
      status
    }
  }
`);

const cashManagementStrategiesListDocument = graphql(`
  query AdminCashManagementStrategiesList(
    $after: String
    $first: Int!
    $filter: CashManagementStrategiesPageFilter!
  ) {
    cashManagementStrategies(after: $after, first: $first, filter: $filter) {
      pageInfo {
        endCursor
        hasNextPage
      }
      cashManagementStrategies {
        ...AdminCashManagementStrategyRow
      }
    }
  }
`);

function View() {
  const navigate = useNavigate();
  const { useSearchParams } = useRouter();

  const [filter, setFilter] = useQueryPersistedStateWithSchema({
    useSearchParams,
    schema: z.object({
      organisationIds: z.array(z.string()).optional().nullable(),
      status: z
        .array(z.enum(cashManagementStrategyStatusValues))
        .optional()
        .nullable(),
    }),
    defaultState: {},
  });

  const baseInput = {
    first: 20,
    filter,
  };

  const { fetchMore, loading, networkStatus, data } = useQuery(
    cashManagementStrategiesListDocument,
    {
      variables: {
        after: null,
        ...baseInput,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const strategies =
    data?.cashManagementStrategies?.cashManagementStrategies ?? [];

  const nextCursor = data?.cashManagementStrategies?.pageInfo.hasNextPage
    ? (data.cashManagementStrategies.pageInfo.endCursor ?? undefined)
    : undefined;

  const { data: pageData } = useSuspenseQuery(
    graphql(`
      query AdminCashManagementStrategiesPage {
        organisations {
          id
          name
        }
      }
    `),
  );

  const organisationOptions =
    pageData.organisations
      ?.map((organisation) => ({
        label: organisation.name,
        value: organisation.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) ?? [];

  return (
    <AdminPageContainer testId="admin-cash-management-strategies">
      <AdminPageHeader
        title="Cash Management Strategies"
        subtitle="Generated strategy drafts and published plans across organisations."
        buttons={
          <Button
            onClick={() =>
              void navigate({
                to: `/cash-management-strategy/${typeid("cash_mgt_strategy")}`,
              })
            }
          >
            Generate
          </Button>
        }
      />
      <div>
        <FilterBar
          filterValue={filter}
          onFilterChange={(values) => {
            setFilter({
              ...values,
            });
          }}
          organisationOptions={organisationOptions}
        />
        <DataTable
          data={strategies}
          columns={columns}
          isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
          onRowClick={(row) =>
            void navigate({
              to: "/cash-management-strategy/$id",
              params: { id: row.original.id },
            })
          }
          manualSorting={true}
          manualPagination={true}
          hidePagination={true}
          pageSize={10_000}
          className="overflow-hidden border"
        />
      </div>
      <div className="mt-4 flex w-full flex-row justify-center">
        {nextCursor ? (
          <Button
            onClick={() =>
              fetchMore({ variables: { ...baseInput, after: nextCursor } })
            }
            variant="outline"
            loading={loading || networkStatus === NetworkStatus.fetchMore}
          >
            Load more
          </Button>
        ) : (
          !!strategies.length && (
            <p className="text-secondary text-sm">No more data</p>
          )
        )}
      </div>
    </AdminPageContainer>
  );
}

type StrategyRow = NonNullable<
  NonNullable<
    AdminCashManagementStrategiesListQuery["cashManagementStrategies"]
  >["cashManagementStrategies"]
>[number];

const columns: ColumnDef<StrategyRow>[] = [
  {
    id: "status",
    accessorFn: (row) => row.status,
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => <StatusUI status={row.original.status} />,
  },
  {
    id: "organisation",
    accessorFn: (row) => row.organisation?.id,
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Organisation" />
    ),
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <Icon
          size="8"
          imageUrl={row.original.organisation?.squareLogoUrl ?? undefined}
          fallbackIcon={<PiBuildingOffice />}
          bordered={false}
        />
        <div className="flex flex-col items-start">
          <Txt variant="name">{row.original.organisation?.name}</Txt>
          <Txt variant="caption">#{row.original.shortRef}</Txt>
        </div>
      </div>
    ),
  },
  {
    id: "updatedAt",
    accessorFn: (row) => row.updatedAt,
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Updated" />
    ),
    cell: ({ row }) => <DateUI date={row.original.updatedAt} delta />,
  },
  {
    id: "createdAt",
    accessorFn: (row) => row.createdAt,
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: ({ row }) => <DateUI date={row.original.createdAt} delta />,
  },
  {
    id: "checklist",
    accessorFn: (row) => row.checklist?.length ?? 0,
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Checklist" />
    ),
    cell: ({ row }) => {
      const checklistCount = row.original.checklist?.length ?? 0;
      const openChecklistCount =
        row.original.checklist?.filter(
          (item) => item.status !== "DONE" && item.status !== "CANCELLED",
        ).length ?? 0;

      return (
        <div className="flex flex-col">
          <Txt>{checklistCount} items</Txt>
          <Txt variant="caption">{openChecklistCount} open</Txt>
        </div>
      );
    },
  },
];

function FilterBar({
  filterValue,
  onFilterChange,
  organisationOptions,
}: {
  filterValue: CashManagementStrategiesPageFilter;
  onFilterChange: (_: CashManagementStrategiesPageFilter) => void;
  organisationOptions: {
    value: string;
    label: React.ReactNode;
  }[];
}) {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState<string>("status");
  const filterCount =
    ((filterValue.organisationIds?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue.status?.length ?? 0) > 0 ? 1 : 0);

  const resetFilter = () => {
    onFilterChange({
      organisationIds: undefined,
      status: undefined,
    });
  };

  const statusOptions = cashManagementStrategyStatusValues.map((status) => ({
    value: status,
    label: <span>{prettyPrintEnum(status)}</span>,
  }));

  return (
    <div className="flex w-full flex-row gap-2">
      <Popover open={open} onOpenChange={(nextOpen) => setOpen(nextOpen)}>
        <PopoverTrigger asChild>
          <Button
            leftIcon={<RiFilter3Line />}
            variant="outline"
            size="sm"
            className="mb-4 h-8 w-fit text-primary"
          >
            Filter{" "}
            {filterCount > 0 && (
              <span className="ml-2">
                <span className="text-info">({filterCount})</span>
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="flex h-[420px] w-[760px] p-0" align="start">
          <div className="flex w-[260px] flex-col justify-between rounded-l-md border-r bg-neutral-muted p-6">
            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("status")}
              >
                <RiPriceTagLine />
                <div className="flex flex-1">Status</div>
                {(filterValue.status?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("organisationIds")}
              >
                <RiBuilding2Line />
                <div className="flex flex-1">Organisation</div>
                {(filterValue.organisationIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 bg-primary text-primary"
              onClick={resetFilter}
            >
              Reset all filters
            </Button>
          </div>
          <div className="flex-1">
            {path === "status" && (
              <ControlledFacetedFilterControl
                options={statusOptions}
                selectedValues={filterValue.status ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    status: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "organisationIds" && (
              <ControlledFacetedFilterControl
                options={organisationOptions}
                selectedValues={filterValue.organisationIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    organisationIds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
          </div>
        </PopoverContent>
      </Popover>

      <ControlledFactedFilterSummary
        title="Status"
        options={statusOptions}
        selectedValues={filterValue.status ?? []}
        onClick={() => {
          setPath("status");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Organisation"
        options={organisationOptions}
        selectedValues={filterValue.organisationIds ?? []}
        onClick={() => {
          setPath("organisationIds");
          setOpen(true);
        }}
      />

      {filterCount > 0 && (
        <Button
          rightIcon={<X />}
          variant="ghost"
          size="sm"
          onClick={resetFilter}
        >
          Reset
        </Button>
      )}
    </div>
  );
}
